import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {msg} from '@lit/localize';
import store from '../../../store';
import {conditional, conditionalWithConditions} from '../../../util/conditional';
import {getProductData} from '../../../util/getProductData';
import {getValueOfFeature, pimText, pimTextDebug} from '../../../util/values';
import {selectSpcConfiguration} from '../../../selectors';
import {olivioHeadGoboApertureOptions, olivioHeadTypeOptions} from '../../olivioModel';

@customElement('olivio-specsheet-head-page')
export class OlivioSpecsheetHeadPage extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
			}

			.pageContainer {
				display: flex;
				justify-content: space-between;
				width: 100%;
				gap: 1cqw;
				padding-top: 2cqw;
			}

			.drawing {
				width: 30%
			}

			.dataList {
				width: 70%;
				container-type: inline-size;
			}

			.imageContainer {
				text-align: center;
				width: 80%;
				margin-left: 0;
				margin-right: auto;
			}

			img {
				max-width: 100%;
			}

			.productImage {
				max-width: 100%;
			}

			.heads {
				font: var(--font-specsheet-print-small-text);
				font-size: 1.2cqw;
				display: flex;
				flex-direction: row;
				gap: 1cqw;
				align-items: center;
			}
			.head {
				display: flex;
				justify-content: center;
				align-items: center;
				background-color: black;
				color: white;
				height: 2cqw;
				width: 2cqw;
				border-radius: 1cqw;
			}
		`]
	;

	@property({type: Object})
	headData = null;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.lang = state.status.lang;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		this.housingSize = this.headData.head.housingSize;

		const reflectorColour = getValueOfFeature('root::LE_ReflectorColour_1', this.spcConfiguration);
		const dimmingLevel = getValueOfFeature('root::LE_DimmingLevel_1', this.spcConfiguration);
		const accessory = getValueOfFeature('root::LE_Accessories_1', this.spcConfiguration);
		const conditions = [
			'HS_HousingSize.' + this.housingSize,
			'LE_ColourTemperature_1.' + this.colourTemperature,
			'LE_LightDistribution_1.' + this.lightDistribution,
			'LE_ReflectorColour_1.' + reflectorColour,
			'LE_DimmingLevel_1.' + dimmingLevel,
			'LE_Accessories_1.' + accessory
		];
		this.image = conditionalWithConditions(conditions, this.conditionalVariantValues, 'REDTAB.RETURN_LU_Image', 'EditorialTable', 1);
		this.sketch = conditionalWithConditions(conditions, this.conditionalVariantValues, 'REDTAB.RETURN_LU_Sketch', 'EditorialTable', 1);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id, 'EditorialTable', 1);
	}

	render() {
		const type = this.headData.head.type;
		const olivioHeadTypeOption = olivioHeadTypeOptions.find((option) => option.id === this.headData.head.type);
		const typeLabel = olivioHeadTypeOption.label[this.lang];
		const typeInfo = olivioHeadTypeOption.info[this.lang];
		const typeDescription = olivioHeadTypeOption.description[this.lang];
		const goboAperture = this.headData.head.goboAperture;

		let goboApertureDescription;
		let goboApertureNumber;
		if (type === 'gobo') {
			const goboApertureOption = olivioHeadGoboApertureOptions.find((option) => option.id === goboAperture);
			goboApertureDescription = goboApertureOption.description[this.lang];
			goboApertureNumber = goboApertureOption.number;
		}
		return html`
			<div class="heads">${msg('Heads')}
				${repeat(this.headData.indexes, (i) => i, (i) => html`
						<span class="head">${i+1}</span>
					`)}
			</div>
			<div class="pageContainer">

				<div class="drawing">
					<div class="imageContainer">
						${when(this.image !== '', () => html`
							<img class="productImage" alt="product image" src="${this.image}"/>
						`)}
					</div>
					<div class="imageContainer">
						${when(this.sketch !== '', () => html`
							<img class="productImage" alt="product image" src="${this.sketch}"/>
						`)}
					</div>
				</div>

				<div class="dataList">
					<olivio-specsheet-head-data .headData="${this.headData}"></olivio-specsheet-head-data>
					${when(type === 'luminaire', () => html`
						<olivio-specsheet-general-data></olivio-specsheet-general-data>
					`)}
					${when(type === 'gobo', () => html`
						<shared-specsheet-feature>
							<span slot="label"></span>
							<span slot="value">
								${typeDescription},<br><br>
								${goboApertureDescription}, ${goboApertureNumber}
							</span>
						</shared-specsheet-feature>
					`)}
					${when(type === 'camera' || type === 'speaker', () => html`
						<shared-specsheet-feature>
							<span slot="label"></span>
							<span slot="value">
								${typeDescription}
							</span>
						</shared-specsheet-feature>
					`)}
				</div>
			</div>
        `;
	}
}
